import _ from 'lodash'
import VisibilityType from '../models/enums/VisibilityType'
import Event from '../models/Event'
import configuration from '../configuration'
import * as errors from '../errors'
import NetworkStatus from '../network/NetworkStatus'
import { arrayWrap } from '../utils'
import BaseService from './BaseService'

const DO_NOT_ACK = [
  'message:is_typing',
  'presence',
  'remote_wipe',
  'sse:heartbeat',
]

const DO_NOT_LOG = [
  'message:is_typing',
  'presence',
  'sse:heartbeat',
]

const STATIC_EVENT_IDS = [
  '00000000-0000-0000-0000-000000000001'
]

const IMMEDIATE_EVENTS = [
  'remote_wipe',
]

const EVENT_FEATURES_BY_CONFIG_KEY = {
  autoDeliver: 'auto-deliver',
  canConfirm: 'can-confirm',
  closeAfterMessages: 'close-after-messages',
  heartbeat: 'heartbeat_5',
  hidden: 'hidden',
  includeDistributionListEvents: 'dl_sender_event',
  isTyping: 'is-typing',
  keepAlive: 'keep_alive_15_1',
  msgDeliveryDelay: 'msg_delivery_delay',
  multiOrg: 'multi-org',
  noOfflineMessages: 'no-offline-messages',
  renderMetadata: 'render_metadata',
}

const PROFILE_BY_ENDPOINT_TYPE = {
  events: {
    allowManualRequestBatch: true,
    basicAuthParam: 'auth_basic',
    features: null,
    requiresBasicAuth: false,
    routerName: 'eventRouter',
    url: '/events',
  },

  simpleNotifications: {
    allowManualRequestBatch: false,
    basicAuthParam: 'auth',
    features: {
      heartbeat: true
    },
    requiresBasicAuth: true,
    routerName: 'notificationRouter',
    url: '/events/simple_notifications',
  },
}

export default class EventsService extends BaseService {
  constructor(host, options) {
    super(host, options)

    if (!this.endpointType) this.endpointType = 'events'

    for (const [key, val] of Object.entries(PROFILE_BY_ENDPOINT_TYPE[this.endpointType])) {
      if (key !== 'url') this[key] = val
    }
  }

  mounted() {
    this._throttled_flushQueuedAcks = _.throttle(
      this._flushQueuedAcks,
      this.config.flushAckInterval,
      { leading: false },
    )

    this.__allEvents = []
    this._connectionRetryCount = 0
    this._eventQueue = []
    this._eventQueueDeferred = false
    this._eventQueueIsManual = false
    this._eventQueueTimeout = null
    this._heartbeatInterval = this.config.eventsHeartbeatIntervalVisible
    this._heartbeatTimeout = null
    this._openTimeout = null
    this._pendingAckConnections = 0
    this._processedEventIds = {}
    this._queuedAcks = []
    this._reconnectWhenQueuesAreEmpty = false
    this._reconnectWhenQueuesAreEmpty = false
    this._retryHistoryTimeout = null
    this._visibilityState = VisibilityType.VISIBLE
    this.isConnected = false
    this.isOpen = false

    if (process.env.NODE_TARGET === 'web') {
      const Visibility = require('visibilityjs')
      this.visibilityListenerId = Visibility.change(this._onVisibilityChange)
    }
  }

  dispose() {
    this._throttled_flushQueuedAcks.cancel && this._throttled_flushQueuedAcks.cancel()

    this.disconnect()

    if (process.env.NODE_TARGET === 'web' && this.visibilityListenerId !== null) {
      const Visibility = require('visibilityjs')
      Visibility.unbind(this.visibilityListenerId)
      this.visibilityListenerId = null
    }
  }

  get _autoAck() {
    return this.endpointType === 'events' && this.config.autoAckSuccessfullyHandledEvents
  }

  get hasConnection() {
    return !!this.eventSource
  }

  get isSignedIn() {
    return !!this._auth
  }

  get router() {
    return this.host[this.routerName]
  }

  setAuth(auth) {
    this._auth = { basicAuthParam: this.basicAuthParam, ...auth }
  }

  removeAuth() {
    this._auth = null
  }

  getAuth() {
    return this._auth
  }

  connect() {
    if (!this.isSignedIn) {
      this._cancelSessionTimers()
      throw new errors.NotSignedInError()
    }

    if (this.requiresBasicAuth && (!this._auth.key || !this._auth.secret)) {
      this._cancelSessionTimers()
      throw new errors.RequiresBasicAuthError()
    }

    if (this.eventSource) {
      throw new Error('Already connected to EventSource')
    }

    const featuresArray = []
    for (const [key, val] of Object.entries(this.features || this.config.events)) {
      if (!val && val !== 0) continue

      const resolvedKey = EVENT_FEATURES_BY_CONFIG_KEY[key]
      const toPush = val === true ? resolvedKey : `${resolvedKey}:${val}`
      featuresArray.push(toPush)
    }

    const features = featuresArray.join(',')

    this.logger.info(
      `connecting ${this.endpointType}. networkStatus=${this.httpClient.networkStatus} features=${features}`
    )

    const isFreshConnect = !this._watchingNetworkConnection
    if (this.httpClient.networkStatus !== NetworkStatus.OFFLINE && isFreshConnect) {
      this.httpClient._setNetworkStatus(NetworkStatus.CONNECTING)
    }

    this._watchNetworkConnection()

    if (this.httpClient.networkStatus === NetworkStatus.OFFLINE) {
      throw new Error('Cannot connect while network is offline, but will autoconnect when online')
    }

    this._isClosed = false
    this._isHeartbeatTimeout = false
    this._reconnectWhenQueuesAreEmpty = false
    this._requestedClose = false
    let eventSource
    let hasConnected = false
    let openTimedOut = false

    this._openTimeout = setTimeout(() => {
      this._openTimeout = null
      openTimedOut = true
      eventSource.close()
    }, this.config.eventsOpenTimeout)

    this.eventSource = eventSource = this.host.eventSourceClient.createEventSource(this.url, {
      auth: this._auth,
      features,
      onOpen: () => {
        this.logger.info('open')
        this._cancelSessionTimers()
        this._setHeartbeatTimeout()
        hasConnected = true
        this.isOpen = true
        this.emit('opened')
      },
      onMessage: (event, { isFirstMessage }) => {
        this._setHeartbeatTimeout()

        if (isFirstMessage) {
          this._setRetryHistoryTimeout()
          this.isConnected = true
          this.emit('connected')
        }

        this._queueEvent(event)
      },
      onError: (err) => {
        const isUnauthorized = err ? this.httpClient.isUnauthorizedError(err) : false
        const isHeartbeatTimeout = this._isHeartbeatTimeout
        const networkStatus = this.httpClient.networkStatus
        const requestedClose = this._requestedClose

        if (isHeartbeatTimeout) {
          err = new Error(`No activity within ${this._heartbeatInterval} milliseconds; reconnecting`)
        } else if (openTimedOut) {
          err = new Error(`Open timed out after ${this.config.eventsOpenTimeout} milliseconds; reconnecting`)
        }

        this.logger.info(
          'error', err, err.message || '', err.code || '',
          err.status !== undefined ? err.status : '',
        )

        this.__allEvents.push({
          clientDisconnect: {
            error: {
              code: err.code,
              message: err.message,
              status: err.status,
            },
            hasConnected,
            isHeartbeatTimeout,
            isUnauthorized,
            networkStatus,
            openTimedOut,
            requestedClose,
            timestamp: new Date(),
          }
        })

        if (!requestedClose) {
          if (isUnauthorized) {
            this.disconnect({ isRecoverable: false })
          } else if (networkStatus !== NetworkStatus.OFFLINE) {
            if (!hasConnected && this._connectionRetryCount === 0) {
              this._connectionRetryCount = 1
            }

            this._disconnectAndRetry()
          }
        }
      }
    })

    this.eventSource.start()
  }

  _scheduleConnectRetry() {
    this._cancelSessionTimers()
    this.logger.info('scheduleConnectRetry', this._connectionRetryCount)

    const waitTime = Math.min(
      this.config.eventsAutoReconnectDelay * this._connectionRetryCount,
      this.config.eventsAutoReconnectMaxDelay
    )

    this._scheduleConnectRetryTimeout = setTimeout(() => this.connect(), waitTime)
    this._connectionRetryCount++
  }

  _watchNetworkConnection() {
    if (this._watchingNetworkConnection) return
    this._watchingNetworkConnection = true
    this.host.on('networkStatus:change', this.onNetworkStatusChanged)
  }

  _unwatchNetworkConnection() {
    if (!this._watchingNetworkConnection) return
    this.host.removeListener('networkStatus:change', this.onNetworkStatusChanged)
    this._watchingNetworkConnection = false
  }

  onNetworkStatusChanged = ({ networkStatus, previousStatus }) => {
    this.__allEvents.push({
      networkStatusChange: {
        previousStatus,
        networkStatus,
        timestamp: new Date(),
      }
    })

    if (networkStatus === NetworkStatus.CONNECTING) {
      // pause a couple seconds, since network may not be fully back up yet
      this._connectionRetryCount = 1
      this._disconnectAndRetry()
    } else if (networkStatus === NetworkStatus.OFFLINE) {
      this._connectionRetryCount = 0
      this.disconnect({ unwatchNetwork: false })
    }
  }

  _cancelSessionTimers() {
    if (this._heartbeatTimeout) {
      clearTimeout(this._heartbeatTimeout)
      this._heartbeatTimeout = null
    }

    if (this._openTimeout) {
      clearTimeout(this._openTimeout)
      this._openTimeout = null
    }

    if (this._scheduleConnectRetryTimeout) {
      clearTimeout(this._scheduleConnectRetryTimeout)
      this._scheduleConnectRetryTimeout = null
    }

    if (this._eventQueueTimeout !== null) {
      clearTimeout(this._eventQueueTimeout)
      this._eventQueueTimeout = null
    }
  }

  _setHeartbeatTimeout(duration) {
    if (this._heartbeatTimeout) {
      clearTimeout(this._heartbeatTimeout)
    }

    if (duration !== undefined) {
      this._heartbeatInterval = duration
    }

    this._heartbeatTimeout = setTimeout(() => {
      this._heartbeatTimeout = null
      this._isHeartbeatTimeout = true
      if (this._connectionRetryCount === 0) this._connectionRetryCount = 1
      this._disconnectAndRetry()
    }, this._heartbeatInterval)
  }

  _cancelRetryHistoryTimeout() {
    if (this._retryHistoryTimeout) {
      clearTimeout(this._retryHistoryTimeout)
      this._retryHistoryTimeout = null
    }
  }

  _setRetryHistoryTimeout() {
    if (this._retryHistoryTimeout) {
      clearTimeout(this._retryHistoryTimeout)
    }

    this._retryHistoryTimeout = setTimeout(() => {
      this._retryHistoryTimeout = null
      this._connectionRetryCount = 0
    }, this.config.eventsClearRetryHistoryInterval)
  }

  reconnect() {
    this._connectionRetryCount = 0
    this.disconnect({ unwatchNetwork: false })
    this.connect()
  }

  _disconnectAndRetry() {
    this.disconnect({ clearQueues: false, unwatchNetwork: false })

    if (this.config.eventsAutoReconnect) {
      this._reconnectWhenQueuesAreEmpty = true
    }

    if (
      this.config.eventsAutoReconnect &&
      this._eventQueue.length === 0 &&
      this._pendingAckConnections === 0 &&
      this._queuedAcks.length === 0
    ) {
      this._scheduleConnectRetry()
    }

    if (this._eventQueue.length > 0) {
      this._scheduleEventQueue({ immediate: true })
    }

    if (this._pendingAckConnections > 0 || this._queuedAcks.length > 0) {
      this._flushQueuedAcks()
    }
  }

  disconnect({ clearQueues=true, isRecoverable=true, unwatchNetwork=true }={}) {
    this._cancelSessionTimers()
    this._cancelRetryHistoryTimeout()
    if (unwatchNetwork) this._unwatchNetworkConnection()

    if (this.eventSource) {
      const eventSource = this.eventSource

      this.eventSource = null
      this._requestedClose = true

      if (!this._isClosed) {
        this._isClosed = true
        eventSource.close()
      }

      this.emit('disconnected', { isRecoverable })
    }

    this.isConnected = false
    this.isOpen = false

    if (clearQueues) {
      this._eventQueue = []
      this._queuedAcks = []
    }
  }

  // not used yet, waiting for server support for simple_notifications in TS-5649
  _closeAllConnections = async () => {
    if (this.eventSource) throw new Error('Existing connections must be closed first')

    if (this.endpointType === 'simpleNotifications') {
      const { key, secret } = this._auth
      if (!key || !secret) {
        throw new errors.RequiresBasicAuthError()
      }

      await this.host.api.notifications.closeAllConnections(key, secret)
    } else {
      await this.host.api.events.closeAllConnections()
    }
  }

  _queueEvent({ data }) {
    if (!data) return
    if (IMMEDIATE_EVENTS.some(event => data.includes(event))) {
      let parsedData
      try {
        parsedData = JSON.parse(data)
      } catch (e) {
        this.logger.error(`error parsing SSE payload: <${data}>`)
        this._disconnectAndRetry()
        return
      }

      const xmlns = Object.keys(parsedData.event)[0]
      const eventName = xmlns.replace('tigertext:iq:', '')
      if (IMMEDIATE_EVENTS.includes(eventName)) {
        this.disconnect()

        const eventId = parsedData['event_id']
        this._processImmediateEvent(eventId, data)

        return
      }
    }

    this._eventQueue.push(data)
    this._scheduleEventQueue()
  }

  _processImmediateEvent = async (eventId, data) => {
    this._eventQueue = [data]
    this._eventQueueDeferred = false

    if (this._autoAck) {
      await this.ack([eventId])
    }

    this.processEventQueue()
  }

  _scheduleEventQueue = ({ immediate = false }={}) => {
    if (this.allowManualRequestBatch && this.config.manuallyRequestEventQueueBatch) {
      this._eventQueueIsManual = true
    }

    if (immediate) {
      clearTimeout(this._eventQueueTimeout)
      this._eventQueueTimeout = null
    }

    if (this._eventQueueTimeout === null) {
      const interval = this.config.eventCollectionInterval
      if (immediate || interval === null) {
        if (this._eventQueueIsManual) {
          this._emitBatch({ immediate })
        } else {
          this.processEventQueue(this._eventQueue.length)
        }
      } else {
        const handler = this._eventQueueIsManual ? this._emitBatch : this.processEventQueue
        this._eventQueueTimeout = setTimeout(handler, interval)
      }
    }
  }

  _emitBatch = ({ immediate = false }={}) => {
    this.emit('batch:available', { maxEventBatch: this._eventQueue.length, immediate })
    this._eventQueueTimeout = null
  }

  deferEventQueue = () => {
    this._eventQueueDeferred = true
  }

  resumeEventQueue = () => {
    this._eventQueueDeferred = false
  }

  processEventQueue = (eventBatch = this.config.maxEventBatch) => {
    if (this._eventQueueDeferred || this._eventQueue.length === 0) return

    let line
    let parseSucceeded
    let parsedLine

    try {
      const lines = this._eventQueue.splice(0, eventBatch)

      for (line of lines) {
        parseSucceeded = false
        parsedLine = JSON.parse(line)
        parseSucceeded = true
        this._handleEvent(parsedLine)
      }

      this._eventQueueTimeout = null
      if (this._eventQueue.length > 0) {
        this._scheduleEventQueue()
      } else if (
        this._reconnectWhenQueuesAreEmpty &&
        this._queuedAcks.length === 0 &&
        this._pendingAckConnections === 0
      ) {
        this._scheduleConnectRetry()
      }
    } catch (e) {
      if (!parseSucceeded) {
        // retry the connection, in case we have a faulty server that truncated the JSON payload
        this.logger.error(`error parsing SSE payload: <${line}>`)
        this._disconnectAndRetry()
      } else {
        this.logger.error('error while processing SSE payload', e, e.stack, { data: parsedLine })
        this.disconnect({ isRecoverable: false })
      }
    }
  }

  _handleEvent(data) {
    const eventId = data['event_id']

    if (!this._autoAck) {
      if (this._processedEventIds[eventId] && !STATIC_EVENT_IDS.includes(eventId)) {
        return
      }
    }

    const xmlns = Object.keys(data.event)[0]
    let eventName = xmlns.replace('tigertext:iq:', '')
    const eventData = data.event[xmlns]

    if (this.config.eventsDebugging || !DO_NOT_LOG.includes(eventName)) this.__allEvents.push(data)
    this.logger.debug('incoming', eventName, eventData)
    const event = new Event(eventId, eventName, eventData)

    try {
      this.router.emit(event)
      this.emit('event', event)

      if (this._autoAck) {
        if (!DO_NOT_ACK.includes(eventName)) {
          this.__queueAck(eventId)
        }
      } else {
        this._processedEventIds[eventId] = true
      }
    } catch (e) {
      this.logger.error(`error in event ${eventName}`, e, e.stack, { data: eventData })
      this.disconnect({ isRecoverable: false })
    }
  }

  __queueAck(eventIds) {
    eventIds = arrayWrap(eventIds)
    this._queuedAcks.push(...eventIds)
    this._throttled_flushQueuedAcks()
  }

  _flushQueuedAcks = async () => {
    if (this._queuedAcks.length === 0) return

    if (this._pendingAckConnections >= this.config.flushAckMaxConnections) {
      this._throttled_flushQueuedAcks()
      return
    }

    const payload = this._queuedAcks.splice(0, this.config.flushAckMaxEvents)
    const ackPromise = this.ack(payload)

    if (this._queuedAcks.length > 0) {
      this._throttled_flushQueuedAcks()
    } else {
      try {
        await ackPromise
      } catch (e) {
        // do nothing
      }

      if (this._reconnectWhenQueuesAreEmpty && this._eventQueue.length === 0 && this._pendingAckConnections === 0) {
        this._scheduleConnectRetry()
      }
    }
  }

  _onVisibilityChange = (e, state) => {
    state = state.toUpperCase()
    const previousState = this._visibilityState
    this._visibilityState = state

    if (this.isConnected) {
      this.__allEvents.push({
        visibilityChange: {
          previousState,
          state,
          timestamp: new Date(),
        }
      })

      if (state === VisibilityType.HIDDEN) {
        this.host.configure({ eventCollectionInterval: null })
        this._setHeartbeatTimeout(this.config.eventsHeartbeatIntervalInvisible)
      } else if (state === VisibilityType.VISIBLE) {
        this.host.configure({ eventCollectionInterval: configuration.eventCollectionInterval })
        this._setHeartbeatTimeout(this.config.eventsHeartbeatIntervalVisible)
      }
    }

    this.emit('visibilityChange', e, state)
  }

  async ack(eventIds) {
    this.host.requireUser()
    let isUnauthorized = false

    eventIds = arrayWrap(eventIds)

    this.logger.log('ack', eventIds)
    this._pendingAckConnections++

    try {
      await this.host.api.events.ack(eventIds)
    } catch (err) {
      isUnauthorized = this.httpClient.isUnauthorizedError(err)
      this.logger.warn('some events not acked', eventIds)
    }

    this._pendingAckConnections--

    if (isUnauthorized) {
      this.logger.warn('no longer authorized, terminating session')
      this.disconnect({ isRecoverable: false })
    }
  }

  get url() {
    const url = PROFILE_BY_ENDPOINT_TYPE[this.endpointType].url
    let version

    if (this.endpointType === 'events') {
      version = this.config.condensedReplays ? 'v5' : 'v2'
    } else {
      version = 'v2'
    }

    return `/${version}${url}`
  }
}
